import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const useUrl = <T>(param: string) => {
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const url = new URLSearchParams(search);

  const urlParam = url.get(param);

  function tryParseJSON(value: string): T | string {
    try {
      return JSON.parse(decodeURIComponent(value)) as T;
    } catch (error) {
      return value;
    }
  }

  const [paramValue, setValue] = useState<T | null>(
    urlParam !== null ? (tryParseJSON(urlParam) as T) : null,
  );

  // `setMultipleParams` may be called a tick after the render it came from (for
  // instance once an awaited call resolves), by which time this render's
  // `search` can be stale; the ref always holds the latest one.
  const searchRef = useRef(search);
  searchRef.current = search;

  useEffect(() => {
    const newValue = new URLSearchParams(search).get(param);

    if (newValue !== null) {
      setValue(tryParseJSON(newValue) as T);
    } else {
      setValue(null);
    }
  }, [param, search]);

  function setParamValue(val: T) {
    if (Array.isArray(val) && val.length > 0) {
      url.set(param, encodeURIComponent(JSON.stringify(val)));
    } else if (Array.isArray(val) && val.length <= 0) {
      url.delete(param);
    } else if (val) {
      url.set(param, val as unknown as string);
    } else {
      url.delete(param);
    }
    navigate({ pathname, search: url.toString() });
    setValue(val);
  }

  function setMultipleParams(updated: Record<string, string | null>) {
    const newUrl = new URLSearchParams(searchRef.current);
    for (const [k, v] of Object.entries(updated)) {
      if (v == null) {
        newUrl.delete(k);
      } else {
        newUrl.set(k, v);
      }
    }
    navigate({ pathname, search: newUrl.toString() });
  }

  return { paramValue, setParamValue, setMultipleParams };
};

export default useUrl;
